"use client";

import Link from "next/link";
import { ArrowRight, Package } from "lucide-react";

import { useChuseok } from "@/features/promotions/queries/usePromotions";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function buildThemeHref(idCategory: number): string {
  return `/shopping?idCategory=${idCategory}&order=best`;
}

export function ChuseokContent() {
  const { data, isLoading, isError } = useChuseok();
  const payload = data?.data;
  const themes = payload?.themes ?? [];
  const period = payload?.period;

  return (
    <div className="max-w-screen-xl mx-auto px-4 pb-16">
      <section className="relative overflow-hidden rounded-lg mt-6">
        <div className="aspect-[2.5/1]">
          <img src="/banners/chuseok-2026.webp" alt="" className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="mt-10 flex flex-col gap-4 border-b border-neutral-200 pb-10">
        <div className="text-xs text-neutral-500 tracking-wide">
          추석 사전 발주 기간 ·{" "}
          {period ? `${period.start} ~ ${period.end}` : "2026-08-31 ~ 2026-09-23"}
        </div>
        <h1 className="text-3xl md:text-4xl font-heading font-semibold leading-tight text-neutral-900">
          추석 선물,
          <br />
          지금 준비할 때
        </h1>
        <p className="text-sm md:text-base text-neutral-600 leading-relaxed max-w-2xl">
          감사 선물부터 가벼운 답례품까지. 매장에 어울리는 명절 상품을 테마별로 미리 준비해보세요.
        </p>
      </section>

      {isLoading && (
        <section className="mt-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[4/5] w-full rounded-md" />
            ))}
          </div>
        </section>
      )}

      {isError && (
        <section className="mt-12 flex flex-col items-center gap-4 text-center py-20">
          <Package className="h-12 w-12 text-neutral-300" strokeWidth={1} />
          <p className="text-sm text-neutral-600">기획전 정보를 불러올 수 없습니다.</p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            다시 시도
          </Button>
        </section>
      )}

      {!isLoading && !isError && themes.length > 0 && (
        <section className="mt-10 flex flex-col gap-10">
          {themes.map((theme) => (
            <div key={theme.id} className="flex flex-col gap-5">
              <div className="flex items-end justify-between border-b border-neutral-200 pb-4">
                <div>
                  <span className="text-xs text-neutral-500 tracking-wider">{theme.date}</span>
                  <h2 className="mt-2 text-2xl font-heading font-semibold text-neutral-900">
                    {theme.title}
                  </h2>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                    {theme.description}
                  </p>
                </div>
                <Link
                  href={buildThemeHref(theme.idCategory)}
                  className="group flex items-center gap-1 text-xs text-neutral-700 hover:text-neutral-900 whitespace-nowrap"
                >
                  <span className="hidden sm:inline text-neutral-400 mr-2">
                    {theme.sourceLabel}
                  </span>
                  전체보기
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>

              {theme.products.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6">
                  {theme.products.map((product) => (
                    <Link
                      key={product.id}
                      href={`/product/${product.id}`}
                      className="group flex flex-col gap-2"
                    >
                      <div className="aspect-square overflow-hidden rounded-sm bg-neutral-100">
                        {product.imagePath ? (
                          <img
                            src={product.imagePath}
                            alt={product.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[11px] text-neutral-500 truncate">
                          {product.brandName}
                        </span>
                        <span className="text-sm text-neutral-900 line-clamp-2 leading-snug">
                          {product.name}
                        </span>
                        <span className="mt-1 text-sm font-semibold text-neutral-900">
                          {product.goodsPrice.toLocaleString()}원
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-neutral-400 py-8 text-center">
                  준비 중인 카테고리입니다.
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {!isLoading && !isError && themes.length > 0 && (
        <section className="mt-20 border-t border-neutral-200 pt-10 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-neutral-600">
            추석을 앞두고 반응 좋은 선물 상품을 한 번에 둘러보세요.
          </p>
          <Button asChild size="lg" className="rounded-sm">
            <Link href="/shopping?order=best">전체 인기 상품 보기</Link>
          </Button>
        </section>
      )}
    </div>
  );
}

export const FamilyMonthContent = ChuseokContent;
